import React from 'react'

import { SEO_DEFAULT_LOCALE } from './constants'
import { SeoPage } from './seoPage'
import type { SeoMeta } from './types'
import { useSeoLocale } from './useSeoLocale'

export type SeoLocaleContent = {
  meta: SeoMeta
  Content: React.ComponentType
}

export type SeoContentByLocale = { sv: SeoLocaleContent, en?: SeoLocaleContent }

/**
 * Picks the translation to render.
 *
 * A locale with no copy falls back to Swedish, which readers never reach: getStaticProps
 * is given the same content map and returns notFound for anything outside it.
 */
export const useSeoLocaleContent = (byLocale: SeoContentByLocale): SeoLocaleContent => {
  const locale = useSeoLocale()

  return byLocale[locale] ?? byLocale[SEO_DEFAULT_LOCALE]
}

/**
 * A generated SEO page.
 *
 * Next serves one route file for every locale, so the locale decides which translation
 * renders. The route imports them all; splitting them into a file per language is not
 * possible, since the two urls collapse onto the same route wherever the slugs match, as
 * /tyres/pickup/dodge-ram does.
 */
export const seoRoute = (byLocale: SeoContentByLocale) => {
  const SeoRoutePage = () => {
    const { meta, Content } = useSeoLocaleContent(byLocale)

    return (
      <SeoPage meta={meta}>
        <Content />
      </SeoPage>
    )
  }

  return SeoRoutePage
}
